'use client'

import { setAccentColor } from '@/lib/features/animatedBackground/animatedBackgroundSlice'
import type { RootState } from '@/lib/store'
import { generateThemeCSSVariables, type ThemeMode } from '@/lib/tokens/semantic'
import { createContext, useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

interface ThemeContextValue {
  theme: ThemeMode
  setTheme: (theme: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: ThemeMode
}

/**
 * ThemeProvider
 *
 * Provides theme context and injects CSS variables into the DOM.
 * Manages the data-theme attribute on the document root.
 */
export function ThemeProvider({ children, defaultTheme = 'warm' }: ThemeProviderProps) {
  const dispatch = useDispatch()
  const currentAccent = useSelector((state: RootState) => state.animatedBackground.currentAccent)

  // Use Redux state as source of truth, fallback to default
  const theme = (currentAccent as ThemeMode) || defaultTheme

  const setTheme = (newTheme: ThemeMode) => {
    dispatch(setAccentColor(newTheme))
  }

  // Inject CSS variables into the root element
  useEffect(() => {
    const root = document.documentElement
    const variables = generateThemeCSSVariables(theme)

    // Set data-theme attribute for CSS selectors
    root.setAttribute('data-theme', theme)

    // Inject CSS variables
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, value)
    })

    // Cleanup is not needed as we want variables to persist
  }, [theme])

  const value = useMemo(
    () => ({
      theme,
      setTheme,
    }),
    [theme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * useTheme hook
 *
 * Access the current theme and theme setter from any component.
 */
export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
